import { useMemo } from "react"
import { useFormikContext } from "formik"
import type { SchemaFieldDescription } from "yup"

import { useValidationSchema } from "./SchemaFormik"

function isSchema(value: unknown): value is { describe: (options: { value: unknown }) => SchemaFieldDescription } {
    return typeof value === "object" && value !== null && "describe" in value && typeof value.describe === "function"
}

function descendTo(description: SchemaFieldDescription, segment: string): SchemaFieldDescription | undefined {
    if ("fields" in description) return description.fields[segment]
    if ("innerType" in description && /^\d+$/u.test(segment)) return description.innerType
    return undefined
}

// Inside array item schemas yup reports `.required()` only as `optional: false`, not as a test.
// TODO: `.min(1, "required")` on arrays/strings is treated as optional here, as it was before the marker existed.
function isRequiredDescription(description: SchemaFieldDescription | undefined): boolean {
    if (description === undefined || !("tests" in description)) return false
    return !description.optional || description.tests.some((test) => test.name === "required")
}

// Formik field names use dots and brackets: "address.line1", "items[0].name"
function pathSegments(name: string): string[] {
    return name.split(/[.[\]]+/u).filter(Boolean)
}

type CacheEntry = Readonly<{ validationSchema: unknown; description: SchemaFieldDescription | undefined }>

// One describe() per form render, shared by every field; `values` identity changes on each change.
const descriptionCache = new WeakMap<object, CacheEntry>()

function describeSchema(validationSchema: unknown, values: object): SchemaFieldDescription | undefined {
    const cached = descriptionCache.get(values)
    if (cached && cached.validationSchema === validationSchema) return cached.description

    let description: SchemaFieldDescription | undefined
    try {
        const schema: unknown = typeof validationSchema === "function" ? validationSchema() : validationSchema
        description = isSchema(schema) ? schema.describe({ value: values }) : undefined
    } catch {
        description = undefined
    }
    descriptionCache.set(values, { validationSchema, description })
    return description
}

// Yup resolves `.when(...)` branches against the current values, so conditional requirements are followed too.
export function useRequiredField(name: string): boolean {
    const { values } = useFormikContext<object>()
    const validationSchema = useValidationSchema()

    return useMemo(() => {
        const description = pathSegments(name).reduce<SchemaFieldDescription | undefined>(
            (current, segment) => (current === undefined ? undefined : descendTo(current, segment)),
            describeSchema(validationSchema, values),
        )
        return isRequiredDescription(description)
    }, [validationSchema, values, name])
}
